import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { BookOpen } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CopyableTable } from "@/components/feature/markdown/CopyableTable";
import { urlTransform } from "@/components/feature/markdown/urlTransform";
import { InlineArtefactViewer } from "@/components/feature/artefactsViewer/InlineArtefactViewer";
import { extractArtefactsFromString } from "@/modules/chat/chat.utils";
import type { Artefact } from "@/modules/chat/chat-ui.types";
import type { Citation } from "./types";

interface Props {
  content: string;
  citations: Citation[];
  onCitationClick: (citation: Citation) => void;
}

const CITATION_PATTERN = /\[(?:Id:)?(\d+(?:,\s*(?:Id:)?\d+)*)\](?!\()/g;
const CONSECUTIVE_PATTERN = /(\[\d+\](?:\s*\[\d+\])*|\[\d+(?:,\s*\d+)+\])/;

const CitationPopover: React.FC<{
  indices: number[];
  citations: Citation[];
  onCitationClick: (c: Citation) => void;
}> = ({ indices, citations, onCitationClick }) => {
  const resolved = indices.map(i => citations[i - 1]).filter(Boolean);
  if (resolved.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mx-0.5 inline-flex items-center justify-center rounded border border-solid border-midnight-blue-900 bg-[#585451] px-1 py-0.5 text-xs font-medium text-white transition-colors duration-200 hover:bg-midnight-blue-800"
        >
          <BookOpen size={10} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 border-midnight-blue-800 bg-midnight-blue-900 p-0 text-white"
        side="top"
        align="start"
      >
        <div className="max-h-60 overflow-y-auto">
          {resolved.map(citation => (
            <button
              key={citation.result_id}
              type="button"
              onClick={() => onCitationClick(citation)}
              className="flex w-full items-start gap-2 border-b border-midnight-blue-700 px-3 py-2 text-left text-xs transition-colors last:border-0 hover:bg-midnight-blue-800"
            >
              <BookOpen size={12} className="mt-0.5 shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-white">{citation.document_name}</p>
                <p className="text-gray-300">{citation.column_name}</p>
                <div className="mt-0.5 line-clamp-3 text-gray-400">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{citation.value}</ReactMarkdown>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const processTextWithCitations = (
  text: string,
  citations: Citation[],
  onCitationClick: (c: Citation) => void,
): React.ReactNode[] => {
  if (!citations.length || !CITATION_PATTERN.test(text)) {
    CITATION_PATTERN.lastIndex = 0;
    return [text];
  }
  CITATION_PATTERN.lastIndex = 0;

  const parts: React.ReactNode[] = [];
  const segments = text.split(CONSECUTIVE_PATTERN);

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment) continue;

    let indices = [...segment.matchAll(/\[(\d+)\]/g)].map(m => parseInt(m[1], 10));
    if (indices.length === 0) {
      const commaMatch = segment.match(/^\[(\d+(?:,\s*\d+)*)\]$/);
      if (commaMatch) {
        indices = commaMatch[1].split(/,\s*/).map(s => parseInt(s, 10));
      }
    }

    if (indices.length > 0) {
      const validIndices = indices.filter(idx => idx >= 1 && idx <= citations.length && citations[idx - 1]?.value);
      if (validIndices.length > 0) {
        parts.push(
          <CitationPopover
            key={`cp-${i}`}
            indices={validIndices}
            citations={citations}
            onCitationClick={onCitationClick}
          />,
        );
      }
    } else {
      parts.push(segment);
    }
  }

  return parts;
};

const processChildren = (
  children: React.ReactNode,
  citations: Citation[],
  onCitationClick: (c: Citation) => void,
): React.ReactNode => {
  if (typeof children === "string") {
    return processTextWithCitations(children, citations, onCitationClick);
  }
  if (Array.isArray(children)) {
    return children.map((child, i) =>
      typeof child === "string" ? (
        <React.Fragment key={i}>{processTextWithCitations(child, citations, onCitationClick)}</React.Fragment>
      ) : (
        child
      ),
    );
  }
  return children;
};

export const SnapshotMarkdown: React.FC<Props> = ({ content, citations, onCitationClick }) => {
  const [processedContent, artefacts] = React.useMemo(() => extractArtefactsFromString(content), [content]);

  const artefactMap = React.useMemo(() => new Map<string, Artefact>(artefacts.map(a => [a.id, a])), [artefacts]);

  const renderers = React.useMemo(
    () => ({
      p: ({ children }: { children?: React.ReactNode }) => (
        <p className="mb-2 whitespace-pre-wrap">{processChildren(children, citations, onCitationClick)}</p>
      ),
      h1: ({ children }: { children?: React.ReactNode }) => (
        <h1 className="mb-4 mt-6 whitespace-pre-wrap text-2xl font-bold">
          {processChildren(children, citations, onCitationClick)}
        </h1>
      ),
      h2: ({ children }: { children?: React.ReactNode }) => (
        <h2 className="mb-3 mt-5 whitespace-pre-wrap text-xl font-bold">
          {processChildren(children, citations, onCitationClick)}
        </h2>
      ),
      h3: ({ children }: { children?: React.ReactNode }) => (
        <h3 className="mb-2 mt-4 whitespace-pre-wrap text-lg font-bold">
          {processChildren(children, citations, onCitationClick)}
        </h3>
      ),
      li: ({ children }: { children?: React.ReactNode }) => (
        <li className="relative pl-2">{processChildren(children, citations, onCitationClick)}</li>
      ),
      ul: ({ children }: { children?: React.ReactNode }) => (
        <ul className="my-4 list-[circle] space-y-2 pl-6">{children}</ul>
      ),
      ol: ({ children }: { children?: React.ReactNode }) => (
        <ol className="my-4 list-decimal space-y-2 pl-6">{children}</ol>
      ),
      blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote className="my-4 whitespace-pre-wrap border-l-4 border-gray-300 pl-4 italic">{children}</blockquote>
      ),
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
        if (href?.startsWith("artefact://")) {
          const artefactId = href.replace("artefact://", "");
          const artefact = artefactMap.get(artefactId);
          if (artefact) {
            return <InlineArtefactViewer artefact={artefact} />;
          }
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-gold-600 underline">
            {children}
          </a>
        );
      },
      table: ({ children }: { children?: React.ReactNode }) => <CopyableTable>{children}</CopyableTable>,
      thead: ({ children }: { children?: React.ReactNode }) => (
        <thead className="border-b border-desert-sand-300 bg-ivory-mist-100">{children}</thead>
      ),
      tbody: ({ children }: { children?: React.ReactNode }) => (
        <tbody className="divide-y divide-desert-sand-200">{children}</tbody>
      ),
      tr: ({ children }: { children?: React.ReactNode }) => (
        <tr className="hover:bg-ivory-mist-50 transition-colors">{children}</tr>
      ),
      th: ({ children }: { children?: React.ReactNode }) => (
        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground">{children}</th>
      ),
      td: ({ children }: { children?: React.ReactNode }) => {
        const text = typeof children === "string" ? children : "";
        const matchedCitation = text ? citations.find(c => c.document_name === text.trim()) : undefined;
        if (matchedCitation) {
          return (
            <td className="max-w-[200px] px-4 py-3">
              <button
                type="button"
                onClick={() => onCitationClick(matchedCitation)}
                className="block max-w-full truncate text-left text-brand-gold-600 underline decoration-brand-gold-600/30 underline-offset-2 transition-colors hover:text-brand-gold-800"
                title={text}
              >
                {children}
              </button>
            </td>
          );
        }
        return <td className="px-4 py-3">{processChildren(children, citations, onCitationClick)}</td>;
      },
      strong: ({ children }: { children?: React.ReactNode }) => (
        <strong>{processChildren(children, citations, onCitationClick)}</strong>
      ),
      em: ({ children }: { children?: React.ReactNode }) => (
        <em>{processChildren(children, citations, onCitationClick)}</em>
      ),
    }),
    [citations, onCitationClick, artefactMap],
  );

  return (
    <div className="markdown-content relative space-y-4 break-normal">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={renderers} urlTransform={urlTransform}>
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};
